import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TouchableOpacity, ScrollView, TextInput, Alert, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { useRouter } from 'expo-router';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { volver } from '../utils/volver';
import { preguntarSiNo } from '../utils/preguntar';

export default function DriverBankAccountsScreen() {
  const router = useRouter();
  const { userFirebaseUid } = useAppStore();
  const [accounts, setAccounts] = useState<any[]>([]);
  // `cargando` empieza en true para no enseñar "no tienes cuentas" mientras
  // la primera lectura viaja: ese mensaje, siendo falso, empuja al chofer a
  // volver a escribir una cuenta que ya tenía.
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const [bankName, setBankName] = useState('');
  const [accountType, setAccountType] = useState('AHORROS');
  const [accountNumber, setAccountNumber] = useState('');

  /**
   * TOKE NO ES UN BANCO, ES UN NUMERO DE TELEFONO.
   *
   * Toke (Banco Popular, 2026) cobra con el numero de celular, y llega a quien
   * NO tiene cuenta de banco — que es media flota de motoconchos. Hasta hoy el
   * chofer que cobraba por Toke tenia que escribir "Toke" en la casilla que
   * dice «Ej: Banco Popular, BHD, Banreservas», marcar «Ahorros» (que es
   * mentira) y meter su telefono donde dice «Numero de Cuenta». Al pasajero le
   * salia «AHORROS - 8295420292», que no se entiende.
   *
   * El modelo bueno YA existia en la casa: `paymentAccountsService.ts` distingue
   * BANK / PHONE / CASH desde hace tiempo — pero solo del lado donde el chofer
   * le paga la cuota a Go Safely, no del lado donde el pasajero le paga a el,
   * que es el de TODOS los viajes.
   */
  const esToke = accountType === 'TOKE';

  // LECTURA EN VIVO DEL SERVIDOR.
  // Antes esto llamaba a `listenToDriverProfile`, que está deprecada y devuelve
  // `null` siempre: la lista salía vacía aunque hubiera cuentas guardadas.
  // `listenToMyBankAccounts` lee el propio doc del chofer con el SDK que toca
  // en cada plataforma. Y sí devolvemos la función de baja: antes la
  // suscripción se quedaba viva al salir de la pantalla.
  useEffect(() => {
    if (!userFirebaseUid) {
      setAccounts([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    const unsubscribe = firestoreService.listenToMyBankAccounts(userFirebaseUid, (lista) => {
      setAccounts(lista);
      setCargando(false);
    });
    return () => { try { unsubscribe?.(); } catch {} };
  }, [userFirebaseUid]);

  const handleSaveAccount = async () => {
    // FIX AUDIT #3: sin UID real → NO escribir bajo 'driver_123' (colisión
    // entre choferes, pérdida de payouts). Exigir sesión real. Va lo primero:
    // antes se comprobaba después de armar la cuenta, así que el chofer podía
    // llenar el formulario entero para nada.
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Inicia sesión para administrar tus cuentas bancarias.');
      return;
    }
    // Con Toke el nombre no se teclea: siempre es Toke. Asi el pasajero ve
    // «Toke» y no lo que al chofer se le ocurriera escribir ese dia.
    const banco = esToke ? 'Toke' : bankName.trim();
    const numero = accountNumber.trim();
    if (!banco || !numero) {
      Alert.alert(
        'Faltan datos',
        esToke
          ? 'Escribe el número de teléfono de tu Toke.'
          : 'Escribe el nombre del banco y el número de cuenta.'
      );
      return;
    }
    if (guardando) return; // doble toque en "Guardar"

    setGuardando(true);
    try {
      const newAccount = {
        // Date.now() solo no basta: dos altas en el mismo milisegundo (o desde
        // dos dispositivos) compartirían id y el borrado se llevaría las dos.
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        bankName: banco,
        accountType,
        accountNumber: numero,
      };

      // AÑADE SOBRE LO QUE HAY EN EL SERVIDOR, no sobre lo que tenga la
      // pantalla en memoria. Antes se escribía `[...accounts, nueva]` con
      // `accounts` vacío por el listener roto → cada cuenta nueva borraba
      // todas las anteriores.
      await firestoreService.addMyBankAccount(userFirebaseUid, newAccount);

      // No tocamos `accounts` a mano: el listener trae la lista real del
      // servidor. Pintarla nosotros sería volver a fiarnos de la memoria.
      setIsAdding(false);
      setBankName('');
      setAccountNumber('');
      setAccountType('AHORROS');
      Alert.alert(
        'Listo',
        esToke
          ? 'Tu Toke quedó guardado. Los pasajeros lo verán al terminar el viaje y podrán copiarlo.'
          : 'Cuenta bancaria agregada correctamente.'
      );
    } catch (error) {
      console.error(error);
      Alert.alert('No se pudo guardar', 'Revisa tu conexión e inténtalo de nuevo. La cuenta no se guardó.');
    } finally {
      setGuardando(false);
    }
  };

  const borrarCuenta = async (id: string) => {
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Inicia sesión para administrar tus cuentas bancarias.');
      return;
    }
    try {
      await firestoreService.removeMyBankAccount(userFirebaseUid, id);
      // El listener refresca la lista con lo que quedó en el servidor.
    } catch (error) {
      console.error(error);
      // Antes este error se tragaba en silencio: el chofer veía la cuenta
      // desaparecer de la pantalla y seguía en el servidor.
      Alert.alert('No se pudo eliminar', 'Revisa tu conexión e inténtalo de nuevo. La cuenta sigue guardada.');
    }
  };

  const handleDeleteAccount = (id: string) => {
    const cuenta = accounts.find((a) => a?.id === id);
    // ⚠️ Era `Alert.alert`: en react-native-web `Alert` es una función
    // vacía, así que en el navegador este botón no hacía nada. Ver utils/preguntar.ts.
    void preguntarSiNo(
      'Eliminar cuenta',
      `¿Seguro que quieres eliminar la cuenta ${cuenta?.bankName ?? ''} ${cuenta?.accountNumber ?? ''}? Los pasajeros dejarán de verla para pagarte por transferencia.`,
      'Cancelar',
      'Eliminar'
    ).then((si: boolean) => { if (si) void borrarCuenta(id); });
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Mis Cuentas Bancarias</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.infoBox}>
          <Ionicons name="information-circle" size={24} color={Colors.primary} />
          <Text style={styles.infoText}>
            Estas cuentas se mostrarán a los pasajeros que seleccionen pagar por "Transferencia Bancaria".
          </Text>
        </View>

        {!isAdding ? (
          <>
            {/* Mientras carga NO decimos "no tienes cuentas": no lo sabemos
                todavía, y afirmarlo hace que el chofer duplique la cuenta. */}
            {cargando && (
              <View style={styles.estadoVacio}>
                <ActivityIndicator color={Colors.primary} />
                <Text style={styles.estadoVacioTexto}>Cargando tus cuentas…</Text>
              </View>
            )}

            {!cargando && accounts.length === 0 && (
              <View style={styles.estadoVacio}>
                <Ionicons name="card-outline" size={32} color="#999" />
                <Text style={styles.estadoVacioTexto}>
                  Todavía no tienes cuentas guardadas. Agrega una para que los pasajeros
                  puedan pagarte por transferencia.
                </Text>
              </View>
            )}

            {accounts.map((acc) => (
              <View key={acc.id} style={styles.accountCard}>
                <View style={styles.accountIcon}>
                  <Ionicons
                    name={acc.accountType === 'TOKE' ? 'phone-portrait' : 'business'}
                    size={24}
                    color="#FFF"
                  />
                </View>
                <View style={styles.accountInfo}>
                  <Text style={styles.bankName}>{acc.bankName}</Text>
                  <Text style={styles.accountDetails}>
                    {acc.accountType === 'TOKE'
                      ? `Teléfono - ${acc.accountNumber}`
                      : `${acc.accountType} - ${acc.accountNumber}`}
                  </Text>
                </View>
                <TouchableOpacity onPress={() => handleDeleteAccount(acc.id)}>
                  <Ionicons name="trash-outline" size={24} color="#FF3B30" />
                </TouchableOpacity>
              </View>
            ))}

            <TouchableOpacity style={styles.addBtn} onPress={() => setIsAdding(true)}>
              <Ionicons name="add-circle-outline" size={24} color={Colors.primary} />
              <Text style={styles.addBtnText}>Agregar Nueva Cuenta</Text>
            </TouchableOpacity>
          </>
        ) : (
          <View style={styles.formContainer}>
            {/* El TIPO va primero: decide todo lo de abajo. */}
            <Text style={styles.label}>¿Cómo quieres que te paguen?</Text>
            <View style={styles.typeSelector}>
              <TouchableOpacity 
                style={[styles.typeBtn, accountType === 'AHORROS' && styles.typeBtnActive]}
                onPress={() => setAccountType('AHORROS')}
              >
                <Text style={[styles.typeText, accountType === 'AHORROS' && styles.typeTextActive]}>Ahorros</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.typeBtn, accountType === 'CORRIENTE' && styles.typeBtnActive]}
                onPress={() => setAccountType('CORRIENTE')}
              >
                <Text style={[styles.typeText, accountType === 'CORRIENTE' && styles.typeTextActive]}>Corriente</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.typeBtn, esToke && styles.typeBtnActive]}
                onPress={() => setAccountType('TOKE')}
              >
                <Text style={[styles.typeText, esToke && styles.typeTextActive]}>Toke</Text>
              </TouchableOpacity>
            </View>

            {esToke ? (
              <Text style={styles.ayudaToke}>
                Con Toke te pagan a tu número de celular. No hace falta cuenta de banco.
              </Text>
            ) : (
              <>
                <Text style={styles.label}>Nombre del Banco</Text>
                <TextInput 
                  style={styles.input} 
                  placeholder="Ej: Banco Popular, BHD, Banreservas"
                  value={bankName}
                  onChangeText={setBankName}
                />
              </>
            )}

            <Text style={styles.label}>{esToke ? 'Tu número de Toke' : 'Número de Cuenta'}</Text>
            <TextInput 
              style={styles.input} 
              placeholder={esToke ? 'Ej: 8295420292' : 'Ej: 123456789'}
              keyboardType={esToke ? 'phone-pad' : 'numeric'}
              value={accountNumber}
              // SOLO DIGITOS. El pasajero COPIA este numero para pegarlo en su
              // banco o en Toke: si se cuela un guion o un espacio, el pegado
              // falla del otro lado y el chofer no cobra. Mismo motivo por el
              // que se limpio el campo de tarifa en el mapa.
              onChangeText={(v) => setAccountNumber(v.replace(/[^0-9]/g, ''))}
            />

            <View style={styles.formActions}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => setIsAdding(false)}
                disabled={guardando}
              >
                <Text style={styles.cancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveBtn, guardando && styles.saveBtnDisabled]}
                onPress={handleSaveAccount}
                disabled={guardando}
              >
                <Text style={styles.saveText}>{guardando ? 'Guardando…' : 'Guardar Cuenta'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F5F7FA' },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 20, paddingVertical: 15, borderBottomWidth: 1, borderBottomColor: '#F0F0F0',
    backgroundColor: '#FFF'
  },
  backButton: { padding: 8, marginLeft: -8 },
  headerTitle: { fontSize: 18, fontWeight: 'bold', color: Colors.text },
  container: { padding: 20 },
  infoBox: {
    flexDirection: 'row', backgroundColor: 'rgba(0, 102, 255, 0.1)', padding: 15,
    borderRadius: 12, alignItems: 'center', marginBottom: 20
  },
  infoText: { flex: 1, marginLeft: 10, color: Colors.primary, fontSize: 13, lineHeight: 18 },
  
  accountCard: {
    backgroundColor: '#FFF', borderRadius: 16, padding: 16,
    flexDirection: 'row', alignItems: 'center', marginBottom: 16,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 10, elevation: 2,
  },
  accountIcon: {
    width: 48, height: 48, borderRadius: 12, backgroundColor: '#4CAF50',
    justifyContent: 'center', alignItems: 'center', marginRight: 16,
  },
  accountInfo: { flex: 1 },
  bankName: { fontSize: 16, fontWeight: 'bold', color: '#1A1A1A', marginBottom: 4 },
  ayudaToke: { fontSize: 13.5, color: '#5a647a', lineHeight: 19, marginBottom: 6 },
  accountDetails: { fontSize: 13, color: '#666' },
  
  addBtn: {
    flexDirection: 'row', alignItems: 'center', padding: 16,
    backgroundColor: 'rgba(0, 102, 255, 0.1)', borderRadius: 16, justifyContent: 'center'
  },
  addBtnText: { marginLeft: 10, fontSize: 16, fontWeight: 'bold', color: Colors.primary },

  formContainer: {
    backgroundColor: '#FFF', borderRadius: 16, padding: 20,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 10, elevation: 2,
  },
  label: { fontSize: 14, fontWeight: 'bold', color: Colors.text, marginBottom: 8, marginTop: 15 },
  input: {
    backgroundColor: '#F5F7FA', borderRadius: 12, padding: 15, fontSize: 16, color: Colors.text, borderWidth: 1, borderColor: '#E0E0E0'
  },
  typeSelector: { flexDirection: 'row', gap: 10 },
  typeBtn: {
    flex: 1, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: '#E0E0E0', alignItems: 'center'
  },
  typeBtnActive: { backgroundColor: 'rgba(0, 102, 255, 0.1)', borderColor: Colors.primary },
  typeText: { fontSize: 14, color: '#666', fontWeight: '600' },
  typeTextActive: { color: Colors.primary },

  formActions: { flexDirection: 'row', marginTop: 25, gap: 10 },
  cancelBtn: { flex: 1, padding: 15, borderRadius: 12, backgroundColor: '#F5F7FA', alignItems: 'center' },
  cancelText: { color: '#666', fontWeight: 'bold', fontSize: 16 },
  saveBtn: { flex: 1, padding: 15, borderRadius: 12, backgroundColor: Colors.primary, alignItems: 'center' },
  saveBtnDisabled: { opacity: 0.6 },
  saveText: { color: '#FFF', fontWeight: 'bold', fontSize: 16 },

  estadoVacio: {
    alignItems: 'center', paddingVertical: 28, paddingHorizontal: 10, gap: 10,
  },
  estadoVacioTexto: {
    textAlign: 'center', color: '#666', fontSize: 14, lineHeight: 20,
  },
});
